package com.neurobs.ccabserver.administrator.contexts

import com.neurobs.ccab.{CCABServer, IdentityData}
import com.neurobs.ccabserver.administrator.controller.DataPasswordCache
import com.neurobs.react.{StateAttribute, StateContextBase}
import com.neurobs.js
import com.neurobs.js.ccab.UserManager
import scala.concurrent.ExecutionContext.Implicits.global

case class UserState
(
	user: Option[IdentityData],
	dataPasswords: DataPasswordCache,
	manager: UserManager
) {
	def set(newUser: Option[IdentityData]): UserState = {
		if (user == newUser) this
		else UserState(newUser, dataPasswords, manager)
	}
}

object UserState {
	def apply(user: Option[IdentityData], manager: UserManager, server: CCABServer): UserState = {
		UserState(
			user = user,
			dataPasswords = new DataPasswordCache(user.map(_.legacyAccountId).getOrElse(0), server),
			manager = manager
		)
	}
}

/** React Context that provides access to the logged in user, or None
 * if no user is logged in.
 *
 * This class also handles storing the id token in a cookie for the web
 * server, and deleting that cookie when changing to logged out state.
 * This id token is also stored in local storage so change notifications
 * can be triggered in other instances of the same web app in other browser
 * tabs or windows. Thus, if one logs out, they will all log out.
 * */
class UserContext(state: StateAttribute[UserState]) {
	private def set(newState: UserState): Unit = {
		if (newState ne value) {
			state.setValue(newState)
		}
	}

	def value: UserState = state.value

	def user: Option[IdentityData] = value.user

	def valid: Boolean = user.isDefined

	def setUser(user: Option[IdentityData]): Unit = {
		set(value.set(user))
	}

	/** Instructs Amplify to sign out the user.
	 *
	 * @note This method doesn't change the UserState, but relies
	 *       on change notifications to trigger update which would
	 *       then make actual changes. (Changing things here directly
	 *       results in recursive calls because of the notifications which
	 *       seem to run inline.
	 */
	def logout(): Unit = {
		if (valid) {
			value.manager.logout()
		}
	}
}

object UserContext extends StateContextBase[UserContext, UserState] {

	override protected def defaultStateValue(): UserState = UserState(None, null: DataPasswordCache, null)

	override protected def makeState(a: StateAttribute[UserState]): UserContext = {
		val res = new UserContext(a)
		if (a.value.manager != null) {
			js.http.onUnauthorizedError = { () => a.value.manager.update() }
			a.value.manager.setChangeSubscriber { user =>
				res.setUser(user)
			}
		}
		res
	}
}